import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FiSearch,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiUserCheck,
  FiX,
  FiCheck,
  FiLoader,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiUsers,
  FiMapPin,
  FiBookOpen,
  FiArrowLeft,
  FiUser
} from "react-icons/fi";
import api from "../../../connector/api";

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 50, 100];

export default function SubjectAssignmentTab() {
  // Primary Master State (Employees List)
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Navigation State (Master / Detail)
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teacherDetailAssignments, setTeacherDetailAssignments] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Detail View Filter State
  const [selectedSchoolYearFilter, setSelectedSchoolYearFilter] = useState("");
  const [isFilterYearDropdownOpen, setIsFilterYearDropdownOpen] = useState(false);
  const filterYearDropdownRef = useRef(null);

  // Form / Dropdown Options
  const [subjects, setSubjects] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [classrooms, setClassrooms] = useState([]);

  // Master Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalCount, setTotalCount] = useState(0);
  const [isPageSizeDropdownOpen, setIsPageSizeDropdownOpen] = useState(false);

  // Modal & Form States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);

  const [formData, setFormData] = useState({
    employee: "",
    subject: "",
    school_year: "",
    classroom: "",
    custom_name: ""
  });
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Custom Dropdown Open States inside Modal
  const [openDropdown, setOpenDropdown] = useState(null); // 'subject' | 'employee' | 'classroom' | 'school_year' | null
  const modalDropdownRef = useRef(null);
  const pageSizeRef = useRef(null);

  // Handle outside clicks for dropdown menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterYearDropdownRef.current &&
        !filterYearDropdownRef.current.contains(event.target)
      ) {
        setIsFilterYearDropdownOpen(false);
      }
      if (
        modalDropdownRef.current &&
        !modalDropdownRef.current.contains(event.target)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 1. Fetch Form Dropdown Options
  const fetchFormOptions = async () => {
    try {
      const [subRes, syRes, classRes] = await Promise.allSettled([
        api.get("/academic/subjects/"),
        api.get("/academic/years/"),
        api.get("/academic/classrooms/"),
      ]);

      if (subRes.status === "fulfilled") {
        const data = subRes.value.data;
        setSubjects(Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : []);
      }
      if (syRes.status === "fulfilled") {
        const data = syRes.value.data;
        setSchoolYears(Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : []);
      }
      if (classRes.status === "fulfilled") {
        const data = classRes.value.data;
        setClassrooms(Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Failed to load options:", err);
    }
  };

  // 2. Fetch Master Employees List with Pagination & Search
  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const res = await api.get("/employees/", {
        params: {
          page: currentPage,
          per_page: pageSize, // Matching Django backend parameter
          search: searchQuery || undefined,
        },
      });

      if (res.data && Array.isArray(res.data.results)) {
        setEmployees(res.data.results);
        setTotalCount(res.data.count || 0);
      } else if (Array.isArray(res.data)) {
        setEmployees(res.data);
        setTotalCount(res.data.length);
      }
    } catch (err) {
      console.error("Failed to fetch employees:", err);
    } finally {
      setLoading(false);
    }
  };

  // 3. Fetch Assignments for the selected Teacher (Detail View)
  const fetchTeacherAssignments = async (employeeId, schoolYearId = selectedSchoolYearFilter) => {
    if (!employeeId) return;
    setDetailLoading(true);
    try {
      const params = { employee: employeeId };
      if (schoolYearId) {
        params.school_year = schoolYearId;
      }

      const res = await api.get("/academic/employee-subjects/", { params });
      const data = res.data?.results || (Array.isArray(res.data) ? res.data : []);
      setTeacherDetailAssignments(data);
    } catch (err) {
      console.error("Failed to fetch teacher assignments:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedTeacher) {
      fetchEmployees();
    }
  }, [currentPage, pageSize, searchQuery, selectedTeacher]);

  useEffect(() => {
    fetchFormOptions();
  }, []);

  useEffect(() => {
    if (selectedTeacher) {
      fetchTeacherAssignments(selectedTeacher.id, selectedSchoolYearFilter);
    }
  }, [selectedTeacher, selectedSchoolYearFilter]);

  // Modal Open/Close Handlers
  const handleOpenModal = (item = null, targetEmployeeId = null) => {
    setFormError("");
    setOpenDropdown(null);
    if (item) {
      setEditingItem(item);
      setFormData({
        employee: item.employee || targetEmployeeId || "",
        subject: item.subject || "",
        school_year: item.school_year || "",
        classroom: item.classroom || "",
        custom_name: item.custom_name || ""
      });
    } else {
      setEditingItem(null);
      setFormData({
        employee: targetEmployeeId || selectedTeacher?.id || "",
        subject: "",
        school_year: schoolYears[0]?.id || "",
        classroom: "",
        custom_name: ""
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setOpenDropdown(null);
    setFormData({
      employee: "",
      subject: "",
      school_year: "",
      classroom: "",
      custom_name: ""
    });
    setFormError("");
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!formData.subject) {
      setFormError("Subject is required.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        employee: formData.employee ? Number(formData.employee) : null,
        subject: Number(formData.subject),
        school_year: formData.school_year ? Number(formData.school_year) : null,
        classroom: formData.classroom ? Number(formData.classroom) : null,
      };

      if (editingItem) {
        await api.put(`/academic/employee-subjects/${editingItem.id}/`, payload);
      } else {
        await api.post("/academic/employee-subjects/", payload);
      }

      handleCloseModal();

      if (selectedTeacher) {
        fetchTeacherAssignments(selectedTeacher.id, selectedSchoolYearFilter);
      }
      fetchEmployees();
    } catch (err) {
      console.error("Error saving subject assignment:", err);
      const errMsg =
        err.response?.data?.detail ||
        err.response?.data?.non_field_errors?.[0] ||
        "Failed to save assignment. Please check your inputs.";
      setFormError(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Handler
  const handleDelete = async () => {
    if (!deletingItem) return;
    setSubmitting(true);
    try {
      await api.delete(`/academic/employee-subjects/${deletingItem.id}/`);
      setIsDeleteModalOpen(false);
      setDeletingItem(null);

      if (selectedTeacher) {
        fetchTeacherAssignments(selectedTeacher.id, selectedSchoolYearFilter);
      }
      fetchEmployees();
    } catch (err) {
      console.error("Error deleting assignment:", err);
    } finally {
      setSubmitting(false);
    }
  };

  // Master Pagination Calculations
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const startIndex = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endIndex = Math.min(currentPage * pageSize, totalCount);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleSelectPageSize = (size) => {
    setPageSize(size);
    setCurrentPage(1);
    setIsPageSizeDropdownOpen(false);
  };

  // Helpers to get selected object labels
  const getSelectedSubjectLabel = () => {
    const sub = subjects.find((s) => String(s.id) === String(formData.subject));
    if (!sub) return "Select Subject...";
    return `${sub.code ? `[${sub.code}] ` : ""}${sub.name}`;
  };

  const getSelectedEmployeeLabel = () => {
    const emp = employees.find((e) => String(e.id) === String(formData.employee));
    if (!emp) return "Select Teacher...";
    const name = `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || emp.username;
    return `${name} (${emp.username || emp.email})`;
  };

  const getSelectedClassroomLabel = () => {
    const cls = classrooms.find((c) => String(c.id) === String(formData.classroom));
    if (!cls) return "Select Class...";
    return cls.name || cls.section?.name || `Class #${cls.id}`;
  };

  const getSelectedSchoolYearLabel = () => {
    const sy = schoolYears.find((y) => String(y.id) === String(formData.school_year));
    if (!sy) return "Select Year...";
    return sy.name;
  };

  return (
    <div className="space-y-4">
      {/* ------------------- DETAIL VIEW ------------------- */}
      {selectedTeacher ? (
        <div className="space-y-4">
          {/* Header & Back Action */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-lg border border-zinc-200/80">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setSelectedTeacher(null);
                  setSelectedSchoolYearFilter("");
                }}
                className="p-1.5 rounded-lg border border-zinc-200 hover:bg-zinc-100 text-zinc-600 transition-colors"
                title="Back to Employees List"
              >
                <FiArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <FiUser className="w-4 h-4 text-zinc-500" />
                  <h2 className="text-sm font-bold text-zinc-900">
                    {selectedTeacher.first_name || selectedTeacher.last_name
                      ? `${selectedTeacher.first_name || ""} ${selectedTeacher.last_name || ""}`.trim()
                      : selectedTeacher.username || `Employee #${selectedTeacher.id}`}
                  </h2>
                </div>
                <p className="text-xs text-zinc-500">
                  Teaching {teacherDetailAssignments.length} subject(s)
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Filter By School Year Custom Dropdown */}
              <div className="relative" ref={filterYearDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsFilterYearDropdownOpen(!isFilterYearDropdownOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 hover:bg-zinc-100 rounded-lg text-xs font-medium text-zinc-800 transition-colors"
                >
                  <span className="text-zinc-500">Year:</span>
                  <span>
                    {selectedSchoolYearFilter
                      ? schoolYears.find((y) => String(y.id) === String(selectedSchoolYearFilter))?.name || "Selected Year"
                      : "All Academic Years"}
                  </span>
                  <FiChevronDown
                    className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${
                      isFilterYearDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isFilterYearDropdownOpen && (
                  <div className="absolute right-0 mt-1 w-48 bg-white border border-zinc-200/80 rounded-lg shadow-xl py-1 z-30 max-h-56 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedSchoolYearFilter("");
                        setIsFilterYearDropdownOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 text-xs text-left hover:bg-zinc-50 transition-colors ${
                        selectedSchoolYearFilter === "" ? "font-bold text-zinc-900 bg-zinc-50" : "text-zinc-600"
                      }`}
                    >
                      <span>All Academic Years</span>
                      {selectedSchoolYearFilter === "" && <FiCheck className="w-3.5 h-3.5 text-zinc-800" />}
                    </button>

                    <div className="my-1 border-t border-zinc-100" />

                    {schoolYears.map((year) => (
                      <button
                        key={year.id}
                        type="button"
                        onClick={() => {
                          setSelectedSchoolYearFilter(year.id);
                          setIsFilterYearDropdownOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 text-xs text-left hover:bg-zinc-50 transition-colors ${
                          String(selectedSchoolYearFilter) === String(year.id)
                            ? "font-bold text-zinc-900 bg-zinc-50"
                            : "text-zinc-600"
                        }`}
                      >
                        <span>{year.name}</span>
                        {String(selectedSchoolYearFilter) === String(year.id) && (
                          <FiCheck className="w-3.5 h-3.5 text-zinc-800" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => handleOpenModal(null, selectedTeacher.id)}
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white rounded-lg text-xs font-medium hover:bg-zinc-800 transition-colors shrink-0"
              >
                <FiPlus className="w-3.5 h-3.5" />
                <span>Add Subject to Teacher</span>
              </button>
            </div>
          </div>

          {/* Teacher's Subject Table */}
          <div className="border border-zinc-200/80 rounded-lg overflow-hidden bg-white">
            {detailLoading ? (
              <div className="flex flex-col items-center justify-center py-16 text-zinc-400 space-y-2">
                <FiLoader className="w-6 h-6 animate-spin text-zinc-500" />
                <p className="text-xs font-medium">Loading subjects...</p>
              </div>
            ) : teacherDetailAssignments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-zinc-50/80 border-b border-zinc-200/80 text-[10px] font-bold text-zinc-500 uppercase tracking-wider">
                      <th className="px-4 py-3">Subject / Custom Display</th>
                      <th className="px-4 py-3">Classroom Section</th>
                      <th className="px-4 py-3">Academic Year</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100 text-zinc-700">
                    {teacherDetailAssignments.map((item) => (
                      <tr key={item.id} className="hover:bg-zinc-50/50 transition-colors">
                        <td className="px-4 py-3 font-semibold text-zinc-900">
                          <div>
                            <span>{item.display_name || item.subject_name || `Subject #${item.subject}`}</span>
                            {item.subject_code && (
                              <span className="ml-1.5 text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-600 rounded font-mono">
                                {item.subject_code}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-zinc-600">
                          {item.classroom_name || <span className="text-zinc-300">Unassigned</span>}
                        </td>
                        <td className="px-4 py-3 text-zinc-500">
                          {item.school_year_name || "-"}
                        </td>
                        <td className="px-4 py-3 text-right space-x-1">
                          <button
                            onClick={() => handleOpenModal(item, selectedTeacher.id)}
                            className="p-1 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                            title="Edit Assignment"
                          >
                            <FiEdit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => {
                              setDeletingItem(item);
                              setIsDeleteModalOpen(true);
                            }}
                            className="p-1 rounded text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Delete Assignment"
                          >
                            <FiTrash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center text-zinc-400 space-y-2">
                <FiBookOpen className="w-8 h-8 text-zinc-300" />
                <p className="text-xs font-semibold text-zinc-700">No Subjects Assigned</p>
                <p className="text-[11px] text-zinc-400">
                  {selectedSchoolYearFilter
                    ? "No subjects assigned matching the selected academic year."
                    : "This teacher currently has no assigned subjects."}
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ------------------- EMPLOYEES LIST (MASTER VIEW) ------------------- */
        <>
          {/* Top Action Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="relative w-full sm:w-72">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
              <input
                type="text"
                placeholder="Search teacher..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-800 placeholder-zinc-400 focus:outline-none focus:border-zinc-400 focus:bg-white transition-colors"
              />
            </div>

            <button
              onClick={() => handleOpenModal()}
              className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white rounded-lg text-xs font-medium hover:bg-zinc-800 active:bg-zinc-950 transition-colors shrink-0"
            >
              <FiPlus className="w-3.5 h-3.5" />
              <span>Assign Subject</span>
            </button>
          </div>

          {/* Master Employee Table */}
          <div className="border border-zinc-200/80 rounded-lg overflow-hidden bg-white">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 text-zinc-400 space-y-2">
                <FiLoader className="w-6 h-6 animate-spin text-zinc-500" />
                <p className="text-xs font-medium">Loading employee records...</p>
              </div>
            ) : employees.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-zinc-50/80 border-b border-zinc-200/80 text-[10px] font-bold text-zinc-500 uppercase tracking-wider">
                        <th className="px-4 py-3">Teacher Name</th>
                        <th className="px-4 py-3">Email / Username</th>
                        <th className="px-4 py-3">Total Subjects Taught</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100 text-zinc-700">
                      {employees.map((emp) => {
                        const fullName = `${emp.last_name || ""}, ${emp.first_name || ""}`.trim() || emp.username;
                        const subjectCount = emp.subject_count ?? emp.total_subjects ?? emp.subjects_count ?? 0;

                        return (
                          <tr key={emp.id} className="hover:bg-zinc-50/50 transition-colors">
                            <td className="px-4 py-3 font-semibold text-zinc-900">
                              <button
                                onClick={() => setSelectedTeacher(emp)}
                                className="flex items-center gap-2 hover:text-indigo-600 transition-colors text-left"
                              >
                                <span>{fullName}</span>
                              </button>
                            </td>
                            <td className="px-4 py-3 text-zinc-500">
                              {emp.email || emp.username || "-"}
                            </td>
                            <td className="px-4 py-3 font-semibold text-zinc-800">
                              <span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200/80 rounded-full text-[11px]">
                                {subjectCount} Subject{subjectCount === 1 ? "" : "s"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                <div className="px-4 py-3 border-t border-zinc-200/80 bg-zinc-50/50 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
                  <div className="flex items-center gap-2">
                    <span>Show</span>

                    <div className="relative" ref={pageSizeRef}>
                      <button
                        type="button"
                        onClick={() => setIsPageSizeDropdownOpen(!isPageSizeDropdownOpen)}
                        className="flex items-center justify-between gap-2 px-2.5 py-1 bg-white border border-zinc-200/80 rounded-lg text-xs font-semibold text-zinc-800 hover:bg-zinc-50 focus:outline-none transition-colors"
                      >
                        <span>{pageSize}</span>
                        <FiChevronDown
                          className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${
                            isPageSizeDropdownOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      {isPageSizeDropdownOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-20"
                            onClick={() => setIsPageSizeDropdownOpen(false)}
                          />
                          <div className="absolute bottom-full mb-1 left-0 w-20 bg-white border border-zinc-200/80 rounded-lg shadow-lg py-1 z-30 max-h-48 overflow-y-auto">
                            {PAGE_SIZE_OPTIONS.map((size) => (
                              <button
                                key={size}
                                onClick={() => handleSelectPageSize(size)}
                                className={`w-full flex items-center justify-between px-3 py-1.5 text-xs text-left hover:bg-zinc-50 transition-colors ${
                                  pageSize === size
                                    ? "font-bold text-zinc-900 bg-zinc-50"
                                    : "text-zinc-600"
                                }`}
                              >
                                <span>{size}</span>
                                {pageSize === size && (
                                  <FiCheck className="w-3 h-3 text-zinc-700" />
                                )}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    <span>entries</span>
                    <span className="text-zinc-300 mx-1">|</span>
                    <span>
                      Showing <strong className="text-zinc-800">{startIndex}</strong> to{" "}
                      <strong className="text-zinc-800">{endIndex}</strong> of{" "}
                      <strong className="text-zinc-800">{totalCount}</strong> entries
                    </span>
                  </div>

                  {/* Navigation Controls */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="p-1.5 rounded-md border border-zinc-200/80 bg-white text-zinc-600 hover:bg-zinc-100 disabled:opacity-40 disabled:hover:bg-white transition-colors"
                      title="Previous Page"
                    >
                      <FiChevronLeft className="w-3.5 h-3.5" />
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                          currentPage === page
                            ? "bg-zinc-900 text-white"
                            : "bg-white border border-zinc-200/80 text-zinc-600 hover:bg-zinc-100"
                        }`}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="p-1.5 rounded-md border border-zinc-200/80 bg-white text-zinc-600 hover:bg-zinc-100 disabled:opacity-40 disabled:hover:bg-white transition-colors"
                      title="Next Page"
                    >
                      <FiChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center text-zinc-400 space-y-2">
                <FiUserCheck className="w-8 h-8 text-zinc-300" />
                <p className="text-xs font-semibold text-zinc-700">No Employees Found</p>
                <p className="text-[11px] text-zinc-400 max-w-xs">
                  {searchQuery
                    ? `No employees found matching "${searchQuery}".`
                    : "No employee records registered in the system."}
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* ------------------- ADD / EDIT ASSIGNMENT MODAL ------------------- */}
      {isModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-xs">
            <div className="bg-white rounded-lg font-dynamic-title border border-zinc-200/80 shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-150">
              <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
                <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">
                  {editingItem ? "Edit Subject Assignment" : "Assign Subject"}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="p-1 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-5 space-y-4" ref={modalDropdownRef}>
                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-xs text-red-600">
                    <FiAlertCircle className="w-4 h-4 shrink-0" />
                    <span>{formError}</span>
                  </div>
                )}

                {/* Custom Dropdown: Subject */}
                <div className="relative">
                  <label className="block text-xs font-bold text-zinc-700 mb-1">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setOpenDropdown(openDropdown === "subject" ? null : "subject")}
                    className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 flex items-center justify-between focus:outline-none focus:border-zinc-400 focus:bg-white transition-colors"
                  >
                    <span className={formData.subject ? "text-zinc-800" : "text-zinc-400"}>
                      {getSelectedSubjectLabel()}
                    </span>
                    <FiChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${openDropdown === "subject" ? "rotate-180" : ""}`} />
                  </button>

                  {openDropdown === "subject" && (
                    <div className="absolute left-0 right-0 mt-1 bg-white border border-zinc-200/80 rounded-lg shadow-lg py-1 z-50 max-h-48 overflow-y-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, subject: "" });
                          setOpenDropdown(null);
                        }}
                        className="w-full text-left px-3 py-1.5 text-xs text-zinc-400 hover:bg-zinc-50"
                      >
                        Select Subject...
                      </button>
                      {subjects.map((sub) => (
                        <button
                          key={sub.id}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, subject: sub.id });
                            setOpenDropdown(null);
                          }}
                          className={`w-full flex items-center justify-between px-3 py-1.5 text-xs text-left hover:bg-zinc-50 transition-colors ${
                            String(formData.subject) === String(sub.id) ? "font-bold text-zinc-900 bg-zinc-50" : "text-zinc-700"
                          }`}
                        >
                          <span>{sub.code ? `[${sub.code}] ` : ""}{sub.name}</span>
                          {String(formData.subject) === String(sub.id) && <FiCheck className="w-3 h-3 text-zinc-800" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Custom Dropdown: Assigned Teacher */}
                <div className="relative">
                  <label className="block text-xs font-bold text-zinc-700 mb-1">
                    Assigned Teacher <span className="text-zinc-400 font-normal">(Optional)</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setOpenDropdown(openDropdown === "employee" ? null : "employee")}
                    className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 flex items-center justify-between focus:outline-none focus:border-zinc-400 focus:bg-white transition-colors"
                  >
                    <span className={formData.employee ? "text-zinc-800" : "text-zinc-400"}>
                      {getSelectedEmployeeLabel()}
                    </span>
                    <FiChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${openDropdown === "employee" ? "rotate-180" : ""}`} />
                  </button>

                  {openDropdown === "employee" && (
                    <div className="absolute left-0 right-0 mt-1 bg-white border border-zinc-200/80 rounded-lg shadow-lg py-1 z-50 max-h-48 overflow-y-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, employee: "" });
                          setOpenDropdown(null);
                        }}
                        className="w-full text-left px-3 py-1.5 text-xs text-zinc-400 hover:bg-zinc-50"
                      >
                        Select Teacher...
                      </button>
                      {employees.map((emp) => {
                        const name = `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || emp.username;
                        return (
                          <button
                            key={emp.id}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, employee: emp.id });
                              setOpenDropdown(null);
                            }}
                            className={`w-full flex items-center justify-between px-3 py-1.5 text-xs text-left hover:bg-zinc-50 transition-colors ${
                              String(formData.employee) === String(emp.id) ? "font-bold text-zinc-900 bg-zinc-50" : "text-zinc-700"
                            }`}
                          >
                            <span>{name} ({emp.username || emp.email})</span>
                            {String(formData.employee) === String(emp.id) && <FiCheck className="w-3 h-3 text-zinc-800" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {/* Custom Dropdown: Classroom Section */}
                  <div className="relative">
                    <label className="block text-xs font-bold text-zinc-700 mb-1">
                      Classroom Section <span className="text-zinc-400 font-normal">(Optional)</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setOpenDropdown(openDropdown === "classroom" ? null : "classroom")}
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 flex items-center justify-between focus:outline-none focus:border-zinc-400 focus:bg-white transition-colors"
                    >
                      <span className={formData.classroom ? "text-zinc-800" : "text-zinc-400"}>
                        {getSelectedClassroomLabel()}
                      </span>
                      <FiChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${openDropdown === "classroom" ? "rotate-180" : ""}`} />
                    </button>

                    {openDropdown === "classroom" && (
                      <div className="absolute left-0 right-0 mt-1 bg-white border border-zinc-200/80 rounded-lg shadow-lg py-1 z-50 max-h-48 overflow-y-auto">
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, classroom: "" });
                            setOpenDropdown(null);
                          }}
                          className="w-full text-left px-3 py-1.5 text-xs text-zinc-400 hover:bg-zinc-50"
                        >
                          Select Class...
                        </button>
                        {classrooms.map((cls) => {
                          const clsLabel = cls.name || cls.section?.name || `Class #${cls.id}`;
                          return (
                            <button
                              key={cls.id}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, classroom: cls.id });
                                setOpenDropdown(null);
                              }}
                              className={`w-full flex items-center justify-between px-3 py-1.5 text-xs text-left hover:bg-zinc-50 transition-colors ${
                                String(formData.classroom) === String(cls.id) ? "font-bold text-zinc-900 bg-zinc-50" : "text-zinc-700"
                              }`}
                            >
                              <span>{clsLabel}</span>
                              {String(formData.classroom) === String(cls.id) && <FiCheck className="w-3 h-3 text-zinc-800" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Custom Dropdown: Academic Year */}
                  <div className="relative">
                    <label className="block text-xs font-bold text-zinc-700 mb-1">
                      Academic Year <span className="text-zinc-400 font-normal">(Optional)</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setOpenDropdown(openDropdown === "school_year" ? null : "school_year")}
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 flex items-center justify-between focus:outline-none focus:border-zinc-400 focus:bg-white transition-colors"
                    >
                      <span className={formData.school_year ? "text-zinc-800" : "text-zinc-400"}>
                        {getSelectedSchoolYearLabel()}
                      </span>
                      <FiChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${openDropdown === "school_year" ? "rotate-180" : ""}`} />
                    </button>

                    {openDropdown === "school_year" && (
                      <div className="absolute left-0 right-0 mt-1 bg-white border border-zinc-200/80 rounded-lg shadow-lg py-1 z-50 max-h-48 overflow-y-auto">
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, school_year: "" });
                            setOpenDropdown(null);
                          }}
                          className="w-full text-left px-3 py-1.5 text-xs text-zinc-400 hover:bg-zinc-50"
                        >
                          Select Year...
                        </button>
                        {schoolYears.map((sy) => (
                          <button
                            key={sy.id}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, school_year: sy.id });
                              setOpenDropdown(null);
                            }}
                            className={`w-full flex items-center justify-between px-3 py-1.5 text-xs text-left hover:bg-zinc-50 transition-colors ${
                              String(formData.school_year) === String(sy.id) ? "font-bold text-zinc-900 bg-zinc-50" : "text-zinc-700"
                            }`}
                          >
                            <span>{sy.name}</span>
                            {String(formData.school_year) === String(sy.id) && <FiCheck className="w-3 h-3 text-zinc-800" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1">
                    Custom Name Alias <span className="text-zinc-400 font-normal">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Advanced Mathematics Section A"
                    value={formData.custom_name}
                    onChange={(e) =>
                      setFormData({ ...formData, custom_name: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 placeholder-zinc-400 focus:outline-none focus:border-zinc-400 focus:bg-white transition-colors"
                  />
                </div>

                <div className="pt-2 flex items-center justify-end gap-2 border-t border-zinc-100">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-3 py-1.5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-lg text-xs font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-zinc-900 text-white rounded-lg text-xs font-medium hover:bg-zinc-800 disabled:opacity-50 transition-colors"
                  >
                    {submitting ? (
                      <FiLoader className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <FiCheck className="w-3.5 h-3.5" />
                    )}
                    <span>{editingItem ? "Update Assignment" : "Save Assignment"}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* ------------------- DELETE ASSIGNMENT CONFIRMATION MODAL ------------------- */}
      {isDeleteModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-xs">
            <div className="bg-white rounded-lg font-dynamic-title border border-zinc-200/80 shadow-2xl w-full max-w-sm overflow-hidden p-5 space-y-4">
              <div className="flex items-center gap-3 text-red-600">
                <FiAlertCircle className="w-5 h-5 shrink-0" />
                <h3 className="text-sm font-bold text-zinc-900">Delete Assignment</h3>
              </div>
              <p className="text-xs text-zinc-600">
                Are you sure you want to delete this subject assignment? This action cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-3 py-1.5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-lg text-xs font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={submitting}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {submitting ? (
                    <FiLoader className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <FiTrash2 className="w-3.5 h-3.5" />
                  )}
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}